// React
import { ReactNode, useCallback } from 'react'

// Components
import { BaseTableHeader, BaseLabel, BaseTableFooter, BaseTableAction, BaseTableBody } from 'components'

// Antd
import { ColumnsType } from 'antd/es/table'
import { CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { ITableEventAttrs } from 'interfaces/table'
import { ISharedSupportDocumentTableProps } from './interfaces'
import { ISharedSupportDocument } from 'interfaces/shared'
import { IApiResponsePagination } from 'interfaces/api'

// Mobx
import { observer } from 'mobx-react'

const SharedSupportDocumentTable = observer(
  ({
    data,
    loading,
    onChange,
    onUpload,
    onDelete,
    onDownload,
    noAction,
    withHeader,
    withFooter,
  }: ISharedSupportDocumentTableProps) => {
    // Hook
    const { t } = useTranslation()

    // Common State
    const columns: ColumnsType<ISharedSupportDocument> = [
      {
        key: 'isMandatory',
        title: t('shared.certificateForm.table.mandatory'),
        dataIndex: 'isMandatory',
        align: 'center',
        render: (_, record) => {
          return record.isMandatory ? (
            <CheckCircleOutlined style={{ color: '#00c808' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#ff5858' }} />
          )
        },
      },
      {
        key: 'programTypeName',
        title: t('shared.myDocument.table.program'),
        dataIndex: 'programTypeName',
        render: (_, record) => {
          return `${record.programTypeName} - ${record.documentProofName}`
        },
      },
      {
        key: 'point',
        title: t('shared.myDocument.table.point'),
        dataIndex: 'point',
      },
      {
        key: 'categoryName',
        title: t('shared.myDocument.table.category'),
        dataIndex: 'categoryName',
      },
      {
        key: 'type',
        title: t('shared.myDocument.table.type'),
        dataIndex: 'type',
      },
      {
        key: 'userFileOriginalName',
        title: t('shared.myDocument.table.attachment'),
        dataIndex: 'userOriginalFileName',
        render: (_, record) => {
          return (
            record.userFileBlobId && (
              <BaseLabel
                label={record.userOriginalFileName!}
                className="text-primary font-14 cursor-pointer"
                onClick={() => onDownload && onDownload(record.userFileBlobId!, record.userOriginalFileName!)}
              />
            )
          )
        },
      },
      {
        key: 'x',
        title: t('app.table.action'),
        dataIndex: '',
        align: 'center',
        render: (_, record) => {
          const anotherAction = [
            {
              label: 'shared.certificateForm.action.uploadDocument',
              isUpload: true,
            },
            {
              label: record.fileBlobId! ? 'shared.certificateForm.action.downloadTemplate' : '',
              onClick: () => onDownload(record.fileBlobId!, record.originalFileName!),
            },
            {
              label: record.userFileBlobId! ? 'shared.certificateForm.action.deleteDocument' : '',
              onClick: () => onDelete && onDelete(record.id!),
            },
          ].filter(action => action.label)

          return (
            <div className="flex justify-center text-center-items-center gap-5">
              <BaseTableAction
                isShow={false}
                isDelete={false}
                isEdit={false}
                anotherAction={anotherAction}
                onUpload={file => onUpload && onUpload(record.id!, file)}
              />

              {/* Loader */}
              {record.isActionLoading && <SyncOutlined spin />}
            </div>
          )
        },
      },
    ]

    /**
     * @description Map columns
     *
     * @return {ColumnsType<ISharedSupportDocument>} columns
     */
    const _columns = (): ColumnsType<ISharedSupportDocument> => {
      const blacklistColumnKeys: string[] = []

      // Check if no action
      if (noAction) blacklistColumnKeys.push('x')
      else blacklistColumnKeys.push('userOriginalFileName')

      return columns.filter(column => blacklistColumnKeys.some(blacklistColumn => blacklistColumn !== column.key))
    }

    /**
     * @description Watch any change in table
     *
     * @param {ITableEventAttrs} event
     *
     * @return {void} void
     */
    const onChangeTable = useCallback(
      (event: ITableEventAttrs): void => {
        onChange && onChange(event)
      },
      [onChange],
    )

    /**
     * @description Render Table Header
     *
     * @return {ReactNode} ReactNode
     */
    const renderTableHeader = (): ReactNode => {
      if (withHeader) {
        return (
          <BaseTableHeader
            searchPlaceholder="myDocument.formPlaceholder.documentSearch"
            onChange={onChangeTable}
            loading={loading.isLoading}
          />
        )
      } else {
        return <></>
      }
    }

    /**
     * @description Render Table Body
     *
     * @return {ReactNode} ReactNode
     */
    const renderTableBody = (): ReactNode => {
      let list: ISharedSupportDocument[]

      // Check if data with paging
      if (withFooter) {
        const _data = data as IApiResponsePagination<ISharedSupportDocument[]>['res']['data']
        list = _data.list
      } else {
        const _data = data as ISharedSupportDocument[]
        list = _data
      }

      return (
        <BaseTableBody
          columns={_columns()}
          dataSource={[...list]}
          loading={loading.isLoading}
          rowKey="id"
          pagination={false}
          rowClassName={(record: ISharedSupportDocument) =>
            (!record.userOriginalFileName || !record.userFileBlobId === null) && record.isMandatory ? 'row-required' : ''
          }
          scroll={{ x: 1000 }}
          isBgPrimaryHeader
        />
      )
    }

    /**
     * @description Render Table Footer
     *
     * @return {ReactNode} ReactNode
     */
    const renderTableFooter = (): ReactNode => {
      if (withFooter) {
        const _data = data as IApiResponsePagination<ISharedSupportDocument[]>['res']['data']

        return <BaseTableFooter onChange={onChangeTable} loading={loading.isLoading} data={_data} />
      } else {
        return <></>
      }
    }

    return (
      <>
        {/* Table Header */}
        {renderTableHeader()}

        {/* Table Body */}
        {renderTableBody()}

        {/* Table Footer */}
        {renderTableFooter()}
      </>
    )
  },
)

export { SharedSupportDocumentTable }
